
import { supabase } from "@/integrations/supabase/client";
import { devLog } from "@/utils/devLog";

// In-memory cache for courses
const courseCache = new Map();

/**
 * Fetches detailed course data including modules, lessons, files and FAQs
 * @param courseId The ID of the course to fetch
 * @returns Promise resolving to course data with related information
 */
export async function fetchCourse(courseId: string) {
  const startTime = performance.now();
  devLog("[fetchCourse] Starting course data fetch", { courseId });
  
  // Check memory cache first (fastest)
  if (courseCache.has(courseId)) {
    const cachedData = courseCache.get(courseId);
    const now = Date.now();
    const cacheAge = now - cachedData.timestamp;
    
    // Use memory cache if less than 10 minutes old
    if (cacheAge < 10 * 60 * 1000) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      devLog("[fetchCourse] Using memory cache", { 
        courseId, 
        cacheAge: `${Math.round(cacheAge / 1000)}s`,
        duration: `${duration.toFixed(1)}ms` 
      });
      
      return cachedData.data;
    }
  }
  
  try {
    // Check localStorage cache next (second fastest)
    try {
      const localData = localStorage.getItem(`course_${courseId}`);
      if (localData) {
        const parsed = JSON.parse(localData);
        const cachedAt = new Date(parsed.cachedAt);
        const now = new Date();
        const cacheAge = now.getTime() - cachedAt.getTime();
        
        // If cache is less than 1 hour old, use it
        if (cacheAge < 60 * 60 * 1000) {
          // Update memory cache
          courseCache.set(courseId, {
            data: parsed,
            timestamp: Date.now()
          });
          
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          devLog("[fetchCourse] Used localStorage cache", { 
            courseId,
            cacheAge: `${Math.round(cacheAge / (60 * 1000))} minutes`,
            duration: `${duration.toFixed(1)}ms` 
          });
          
          return parsed;
        }
      }
    } catch (err) {
      // Ignore localStorage errors
    }
    
    // Fetch from network (slowest)
    const { data, error } = await supabase
      .from('courses')
      .select(`
        *,
        creator_id,
        modules (
          id,
          title,
          order,
          lessons (
            id,
            title,
            description,
            video_url,
            is_preview,
            module_id,
            lesson_files (id, file_name, file_url),
            lesson_faqs (id, question, answer)
          )
        )
      `)
      .eq('id', courseId)
      .maybeSingle();
    
    if (error) {
      devLog("[fetchCourse] Error fetching course data", { error });
      throw error;
    }
    
    // Sort modules and count lessons
    const sortedModules = data.modules?.sort((a, b) => a.order - b.order) || [];
    
    // Sort lessons within each module
    sortedModules.forEach(module => {
      if (module.lessons) {
        module.lessons.sort((a, b) => a.id.localeCompare(b.id));
      }
    });
    
    const { data: creatorProfile, error: creatorError } = await supabase
      .from('profiles')
      .select('username, full_name')
      .eq('id', data.creator_id)
      .maybeSingle();
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    const result = {
      ...data,
      modules: sortedModules,
      creatorProfile
    };
    
    devLog("[fetchCourse] Successfully fetched course from network", { 
      courseId, 
      moduleCount: sortedModules.length,
      hasCreatorProfile: !!creatorProfile,
      duration: `${duration.toFixed(1)}ms`
    });
    
    // Cache the course data in memory
    courseCache.set(courseId, {
      data: result,
      timestamp: Date.now()
    });
    
    // Cache the course data in localStorage for faster loading on return visits
    try {
      localStorage.setItem(`course_${courseId}`, JSON.stringify({
        ...result,
        cachedAt: new Date().toISOString()
      }));
    } catch (err) {
      // Ignore storage errors
      devLog("[fetchCourse] Error caching course data", { error: err });
    }
    
    return result;
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    devLog("[fetchCourse] Error in fetchCourse", {
      error: err, 
      duration: `${duration.toFixed(1)}ms`
    });
    
    // Try to load from cache if network request failed
    try {
      const cachedData = localStorage.getItem(`course_${courseId}`);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        devLog("[fetchCourse] Falling back to cached data after error", { courseId });
        return parsed;
      }
    } catch (cacheErr) {
      // Ignore cache errors
    }
    
    // Re-throw the original error if we couldn't load from cache
    throw err;
  }
}
